import { Button, Card, Divider, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccount } from "src/contexts/accountContext";
import { useCart } from "src/contexts/cartContext";
import { useLocalStorageState } from "src/hooks/useLocalStorageState";
import { createAmountFormater } from "src/lib/formating";
import { NexApi } from "src/lib/neuroexchangeApi";

const PAYING_CURRENCIES = ["usd", "eur", "brl"];

export default function CardCheckoutPopup({ onClose }) {
  const cartContext = useCart();
  const items = cartContext.cartItems || [];
  const firstItem = items[0];

  const accountContext = useAccount();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [checkoutId, setCheckoutId] = useLocalStorageState("neuropay.checkout.id", null);
  const [selectedCurrency, setSelectedCurrency] = useState((firstItem?.currency || "brl").toLowerCase());
  const [finalPrice, setFinalPrice] = useState(null);
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState("");
  const { t, i18n } = useTranslation("common");
  const tokenCount = Math.max(1, Math.floor(Number(firstItem?.count || 0)));
  const originalAmount = (cartContext.getItemUnitPrice?.(firstItem) || 0) * tokenCount;

  useEffect(() => {
    if (!firstItem?.currency) {
      return;
    }

    setSelectedCurrency((currentCurrency) => {
      const normalizedFirstCurrency = firstItem.currency.toLowerCase();

      if (!currentCurrency || !PAYING_CURRENCIES.includes(currentCurrency)) {
        return normalizedFirstCurrency;
      }

      return currentCurrency;
    });
  }, [firstItem?.currency]);

  useEffect(() => {
    let isActive = true;

    const loadFinalPrice = async () => {
      const projectId = firstItem?.projectId;

      if (!projectId || tokenCount <= 0 || !selectedCurrency) {
        if (isActive) {
          setFinalPrice(null);
          setPriceError("");
          setIsPriceLoading(false);
        }
        return;
      }

      setIsPriceLoading(true);
      setPriceError("");

      try {
        const country = accountContext.country || "BR";
        const returnUrl = window.location.origin + "/checkoutComplete";
        const returnUrlCancel = window.location.origin + "/checkout";
        const quote = await NexApi.neuroPay.getFinalPrice(
          projectId,
          tokenCount,
          selectedCurrency,
          country,
          returnUrl,
          returnUrlCancel,
        );

        if (!isActive) {
          return;
        }

        setFinalPrice(quote);
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.warn("[checkout] Final price retrieval failed", error);
        setFinalPrice(null);
        setPriceError(error?.message || t("checkout.cardPriceUnavailable", "Could not load the final card price."));
      } finally {
        if (isActive) {
          setIsPriceLoading(false);
        }
      }
    };

    loadFinalPrice();

    return () => {
      isActive = false;
    };
  }, [accountContext.country, firstItem?.count, firstItem?.projectId, selectedCurrency, t]);

  const payingCurrencyCode = (finalPrice?.currency || selectedCurrency || firstItem?.currency || "BRL").toUpperCase();
  const receiveCurrencyCode = (firstItem?.currency || "BRL").toUpperCase();
  const exchangeRate = finalPrice?.conversion?.rate != null
    ? Number(finalPrice.conversion.rate)
    : null;
  const convertedAmount = finalPrice?.price != null
    ? Number(finalPrice.price)
    : null;
  const formatAmount = (amount, currency) => {
    if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
      return "--";
    }

    return createAmountFormater((currency || "BRL").toUpperCase(), i18n.language)(amount);
  };
  const formatRate = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "--";
    }

    return new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(Number(value));
  };


  const goToCheckout = async () => {
    if (isRedirecting) return false;


    const projectId = firstItem?.projectId;

    setIsRedirecting(true);

    try {
      const tabId = window.sessionStorage.getItem("tabId") || "tab-" + Date.now();
      const country = accountContext.country || "BR";
      const returnUrl = window.location.origin + "/checkoutComplete";
      const returnUrlCancel = window.location.origin + "/checkout";

      const { checkoutUrl, checkoutId: gottenCheckoutId } = await NexApi.neuroPay.createCheckout(
        projectId,
        tokenCount,
        selectedCurrency,
        tabId,
        country,
        returnUrl,
        returnUrlCancel,
      );

      setCheckoutId(gottenCheckoutId);

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return true;
      }

      setIsRedirecting(false);
      return false;
    } catch (e) {
      console.warn("[checkout] Payment session creation failed", e);
      setIsRedirecting(false);
      return false;
    }
  };

  return <>
    <Box
      onClick={onClose}
      sx={{
        display: "flex",
        position: "fixed",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100vh",
        top: 0,
        left: 0,
        padding: { xs: "1rem", sm: "1.5rem" },
        backgroundColor: "rgba(15, 23, 42, 0.35)",
        backdropFilter: "blur(8px)",
        zIndex: 1300,
      }}>
      <Card
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: "100%",
          maxWidth: "42rem",
          padding: { xs: "1.5rem", sm: "2rem" },
          borderRadius: "16px",
          background: "linear-gradient(180deg, #f7f8fa 0%, #ffffff 100%)",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
        }}>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="h5" sx={{ fontWeight: 700, textAlign: "center", color: "#111827" }}>
              {t("checkout.payWithCard", "Pay with card")}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: "center", color: "#6b7280" }}>
              {t("checkout.cardPaymentPreview", "Review the final amount before you continue to NeuroPay.")}
            </Typography>
          </Stack>

          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: "14px",
              border: "1px solid rgba(148, 163, 184, 0.24)",
              backgroundColor: "rgba(255, 255, 255, 0.88)",
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ color: "#475569", letterSpacing: "0.02em" }}>
                {t("checkout.currencySelection", "Currency selection")}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
                  gap: { xs: 1.5, sm: 1 },
                  alignItems: "center",
                }}
              >
                <TextField
                  select
                  label={t("checkout.youPayIn", "You pay in")}
                  value={selectedCurrency}
                  onChange={(e) => {
                    setSelectedCurrency(e.target.value);
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#ffffff",
                      transition: "box-shadow 160ms ease, border-color 160ms ease",
                      "&:hover": {
                        boxShadow: "0 0 0 4px rgba(15, 23, 42, 0.04)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.12)",
                      },
                    },
                  }}
                >
                  {PAYING_CURRENCIES.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>

                <Box
                  key={selectedCurrency}
                  sx={{
                    justifySelf: "center",
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    border: "1px solid rgba(148, 163, 184, 0.24)",
                    backgroundColor: "rgba(255,255,255,0.92)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 180ms ease, box-shadow 180ms ease",
                    transform: isPriceLoading ? "scale(0.96) translateX(2px)" : "scale(1)",
                    boxShadow: isPriceLoading ? "0 10px 20px rgba(15, 23, 42, 0.08)" : "none",
                  }}
                >
                  <svg width="28" height="24" viewBox="0 0 50 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 19H0V23H2V21V19ZM50 21L30 9.45299V32.547L50 21ZM2 21V23H32V21V19H2V21Z" fill="#475569" fillOpacity="0.72" />
                  </svg>
                </Box>

                <Box
                  sx={{
                    minHeight: 56,
                    px: 2,
                    py: 1.25,
                    borderRadius: "12px",
                    border: "1px solid rgba(37, 99, 235, 0.28)",
                    backgroundColor: "rgba(239, 246, 255, 0.9)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 0.25,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    {t("checkout.youReceiveIn", "You receive in")}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                    {receiveCurrencyCode}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: "14px",
              border: "1px solid rgba(148, 163, 184, 0.24)",
              backgroundColor: "rgba(255, 255, 255, 0.92)",
              transition: "box-shadow 180ms ease, transform 180ms ease",
              boxShadow: convertedAmount != null ? "0 14px 30px rgba(15, 23, 42, 0.06)" : "none",
              transform: isPriceLoading ? "scale(0.99)" : "scale(1)",
            }}
          >
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "#475569", letterSpacing: "0.02em" }}>
                  {t("checkout.paymentPreview", "Payment preview")}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {isPriceLoading
                    ? t("checkout.loadingFinalPrice", "Loading final price...")
                    : t("checkout.liveQuote", "Live quote")}
                </Typography>
              </Box>

              <Stack spacing={1.25}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    {t("checkout.originalPrice", "Original")}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#0f172a" }}>
                    {formatAmount(originalAmount, receiveCurrencyCode)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    {t("checkout.convertedPrice", "Converted")}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827" }}>
                    {isPriceLoading ? "--" : formatAmount(convertedAmount, payingCurrencyCode)}
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: "rgba(148, 163, 184, 0.2)" }} />
              </Stack>

              {
                !priceError &&
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  {t(
                    "checkout.exchangeRateValue",
                    "Exchange rate: 1 {{from}} = {{rate}} {{to}}",
                    {
                      from: receiveCurrencyCode,
                      rate: formatRate(exchangeRate ?? 1),
                      to: payingCurrencyCode,
                    }
                  )}
                </Typography>
              }

              {
                priceError &&
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "12px",
                    backgroundColor: "rgba(254, 242, 242, 0.9)",
                    border: "1px solid rgba(248, 113, 113, 0.22)",
                  }}
                >
                  <Typography textAlign="center" color="error.main" variant="body2">
                    {priceError}
                  </Typography>
                </Box>
              }
            </Stack>
          </Box>

          <Stack spacing={1.25} alignItems="center">
            <Button
              onClick={goToCheckout}
              disabled={!firstItem || isRedirecting}
              variant="contained"
              size="large"
              fullWidth
              sx={{
                py: 1.35,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1rem",
                backgroundColor: "#0f172a",
                boxShadow: "0 16px 28px rgba(15, 23, 42, 0.2)",
                transition: "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
                "&:hover": {
                  backgroundColor: "#111827",
                  boxShadow: "0 20px 32px rgba(15, 23, 42, 0.24)",
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#94a3b8",
                  color: "rgba(255, 255, 255, 0.88)",
                },
              }}
            >
              {isRedirecting
                ? t("checkout.redirecting", "Redirecting...")
                : t("checkout.continueToCheckout", "Continue to Checkout")}
            </Button>

            <Typography variant="body2" sx={{ textAlign: "center", color: "#6b7280" }}>
              {t("checkout.redirectToProvider", "You will be redirected to NeuroPay to complete the payment.")}
            </Typography>
          </Stack>
        </Stack>
      </Card>
    </Box>
  </>;
}